import React, {useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {SavDetailResponse} from '../../../../components/sav/Models'
import {commenterSavDossier, savErreur} from '../../../../components/sav/requests'
import {formatSavDate, nomSavUtilisateur} from '../../../../components/sav/libelles'
import {SavInformerClientModal} from './SavInformerClient'

type Evenement = {
  date: string
  titre: string
  detail?: string | null
  auteur?: string | null
  type: 'historique' | 'commentaire' | 'message'
}

type Props = {
  detail: SavDetailResponse
  onRecharger: (detail?: SavDetailResponse) => void
}

// Fil des modifications : historique, commentaires et messages envoyés (maquette 0.8)
const SavFilModifications = ({detail, onRecharger}: Props) => {
  const [tousLesDetails, setTousLesDetails] = useState(false)
  const [commentaire, setCommentaire] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [mail, setMail] = useState(false)
  const {dossier} = detail

  const evenements = useMemo(() => {
    const liste: Array<Evenement> = [
      ...detail.historique
        // Sans « tous les détails » : créations et changements de statut seulement
        .filter(
          (h) => tousLesDetails || h.action === 'Création' || h.action === 'Changement de statut'
        )
        .map((h) => ({
          date: h.date,
          titre:
            h.action === 'Création'
              ? 'Création du dossier'
              : h.action === 'Changement de statut'
              ? `Statut : ${h.statut}`
              : h.action,
          detail: h.commentaire,
          auteur: h.utilisateur,
          type: 'historique' as const,
        })),
      ...dossier.commentaires.map((c) => ({
        date: c.dateCreation,
        titre: c.visibleClient ? 'Commentaire (visible du client)' : 'Commentaire',
        detail: c.message,
        auteur: nomSavUtilisateur(c.utilisateur),
        type: 'commentaire' as const,
      })),
      ...dossier.messages.map((m) => ({
        date: m.dateEnvoi,
        titre: `${m.canal === 'MAIL' ? 'Mail' : 'SMS'} envoyé à ${m.destinataire}${
          m.statutEnvoi === 'ECHEC' ? ' (échec)' : ''
        }`,
        detail: m.objet ?? m.corpsEnvoye,
        auteur: nomSavUtilisateur(m.utilisateur),
        type: 'message' as const,
      })),
    ]
    return liste.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [detail, tousLesDetails, dossier])

  const envoyer = () => {
    if (!commentaire.trim()) return
    setEnvoi(true)
    commenterSavDossier(dossier.id, commentaire)
      .then((reponse) => {
        setCommentaire('')
        onRecharger(reponse)
      })
      .catch((e) => toast.error(savErreur(e)))
      .finally(() => setEnvoi(false))
  }

  const icones = {
    historique: 'bi-clock-history text-primary',
    commentaire: 'bi-chat-left-text text-info',
    message: 'bi-envelope text-success',
  }

  return (
    <div className='card mt-5'>
      <div className='card-header align-items-center'>
        <h3 className='card-title fw-bold'>Fil des modifications</h3>
        <div className='form-check form-switch'>
          <input
            className='form-check-input'
            type='checkbox'
            id='savTousDetails'
            checked={tousLesDetails}
            onChange={(e) => setTousLesDetails(e.target.checked)}
          />
          <label className='form-check-label' htmlFor='savTousDetails'>
            Voir tous les détails
          </label>
        </div>
      </div>
      <div className='card-body'>
        <div className='d-flex gap-3 mb-6'>
          <input
            className='form-control form-control-solid'
            placeholder='Écrire un commentaire…'
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && envoyer()}
          />
          <button
            className='btn btn-info btn-icon'
            title='Envoyer le commentaire (interne)'
            onClick={envoyer}
            disabled={envoi || !commentaire.trim()}
          >
            <i className='bi bi-send fs-3'></i>
          </button>
          <button
            className='btn btn-info btn-icon'
            title='Envoyer au client par mail'
            onClick={() => setMail(true)}
            disabled={envoi}
          >
            <i className='bi bi-envelope fs-3'></i>
          </button>
        </div>
        {evenements.length ? (
          evenements.map((e, i) => (
            <div key={i} className='d-flex gap-4 mb-5'>
              <div className='text-muted fs-7 text-nowrap' style={{minWidth: 110}}>
                {formatSavDate(e.date, true)}
              </div>
              <i className={`bi ${icones[e.type]} fs-4`}></i>
              <div>
                <div className='fw-bold'>
                  {e.titre}
                  {e.auteur && <span className='text-muted fw-normal'> · {e.auteur}</span>}
                </div>
                {e.detail && (
                  <div className='text-gray-700' style={{whiteSpace: 'pre-line'}}>
                    {e.detail}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className='text-muted'>Aucun événement.</div>
        )}
      </div>
      {mail && (
        <SavInformerClientModal
          dossier={dossier}
          evenement={null}
          texteLibre={commentaire}
          onClose={() => setMail(false)}
          onTermine={(d) => {
            setCommentaire('')
            onRecharger(d)
          }}
        />
      )}
    </div>
  )
}

export default SavFilModifications
